/**
 * levelgen/surround.ts — The town past the edge of the walk.
 *
 * A generated map used to be the whole universe: the walker was clamped to
 * `world`, and past the last terrace there was nothing but fog over empty
 * ground. That reads as a diorama. A town you can see carrying on in every
 * direction reads as a place, and the walk reads as a walk through part of it.
 *
 * So the street grid is grown outward by a ring of blocks on all four sides,
 * and the *playable* rectangle — `LevelSpec.bounds` — is the part inside that
 * ring. Everything in the ring is drawn and nothing in it is played: no
 * landmark stands there, no critter spawns there, and the reachability flood
 * fill never enters it.
 *
 * ## Why the ring is built by the same code as the town
 *
 * It would be cheaper to fake it — a field of extruded boxes at the horizon.
 * But the ring is seen *through* the same camera at the same tilt as the town
 * proper, from up to a block away in clear air, and anything that isn't built
 * to the same rules shows up immediately: roofs ridged the wrong way, doors
 * onto nothing, terraces that don't line up with the street. So `planStreets`
 * simply lays a bigger grid, and `placePlots` / `placeParks` / `plantTrees`
 * fill the whole of it without being told which parts are scenery.
 *
 * The one thing the ring does *not* get is a lane cut through it (see
 * `cutLanes`): an alley is a route, and a route is only worth having where
 * somebody can walk down it.
 */
import { CAMERA, WORLD } from '../../config/constants';
import { rectsOverlap, type Rect } from '../level';
import type { Range } from './difficulty';
import type { Rng } from './rng';

/**
 * How deep the ring has to be before the *fog* is what ends the view rather
 * than the edge of the map.
 *
 * Fog is measured from the camera, and the camera sits `CAMERA.DISTANCE` back
 * from the walker, so ground `d` ahead of a walker standing on the boundary is
 * `d + CAMERA.DISTANCE` from the camera and therefore
 * `(d + CAMERA.DISTANCE - FOG_NEAR) / (FOG_FAR - FOG_NEAR)` of the way into the
 * haze. Solving that for the point where what's left is too faint to read as
 * anything — about 85% — is where this number comes from.
 *
 * It is a **target**, not a promise: the ring is built out of whole blocks, so
 * the depth that actually lands is whatever the blocks and their corridors add
 * up to. `SURROUND_RINGS` is chosen so that sum clears this comfortably for
 * every band. `surround.test.ts` pins that it does.
 */
export const SURROUND_DEPTH =
  WORLD.FOG_NEAR + (WORLD.FOG_FAR - WORLD.FOG_NEAR) * 0.85 - CAMERA.DISTANCE;

/**
 * How many blocks deep the ring is on each side.
 *
 * Two, and the number is a straight trade between how far the town carries on
 * and how much of it there is to build. One ring of ordinary blocks is about
 * 440 units of depth — the map edge would still be sitting in half-clear air,
 * which is the exact thing this module exists to stop. Three would clear the
 * fog with room to spare and cost another band of town nobody can make out.
 *
 * Ordinary blocks rather than a single deep one, because a deep block only has
 * frontage at its two ends: it would put one terrace along the street and leave
 * a field of several hundred units behind it, which from the boundary reads as
 * the town stopping one row later than it used to rather than carrying on.
 */
export const SURROUND_RINGS = 2;

/**
 * How big a ring block is.
 *
 * Deliberately larger than the town's own blocks, and this is where the cost of
 * the surround is really decided. Depth has to reach `SURROUND_DEPTH`, and there
 * are only two ways to buy it: more blocks, or bigger ones. More blocks is the
 * expensive way — every block is another set of frontages to lay a terrace
 * along, and a ring is twelve of them before the town has been built at all.
 *
 * Bigger is close to free, because what a big block adds is its *middle*, and
 * the middle of a ring block is the one piece of ground nobody will ever see
 * clearly: plots are laid along the frontages, so a deep block gets its terrace
 * on the street exactly like a shallow one and leaves open ground behind it,
 * several hundred units out and correspondingly deep in the haze.
 */
const RING_BLOCK: Range = [420, 560];

/**
 * Block sizes for the ring down one side of the town.
 *
 * Drawn *per block* for the same reason `planStreets` does it: the streets have
 * to run straight through from the ring into the town and out the other side,
 * so the ring's blocks are simply more entries in the same column and row lists.
 *
 * `sizes` is the town's own block range, and is only used to keep the ring from
 * coming out *smaller* than the town it surrounds — on the widest easy town the
 * two ranges can otherwise cross over.
 */
export function ringBlocks(rng: Rng, sizes: Range): number[] {
  const lo = Math.max(RING_BLOCK[0], sizes[1]);
  return Array.from({ length: SURROUND_RINGS }, () => rng.int(lo, Math.max(lo, RING_BLOCK[1])));
}

/**
 * Whether this patch of ground is inside the walk, and so worth planting.
 *
 * The rule that keeps the surround affordable, and the one place the ring is
 * deliberately built to a lower standard than the town.
 *
 * Tree placement is by far the most expensive stage of generation — every
 * candidate position is checked against every tree already planted, so the cost
 * grows with the square of how much ground is being planted — and the ring is
 * several times the area of the town it surrounds. Planting it doubled the tree
 * count and the generation time for foliage that is behind a terrace, several
 * hundred units out, and in fog.
 *
 * So the ring gets streets, terraces and the yard planting the renderer lays
 * from the block interiors, and it does not get trees or parks. What is lost is
 * a back garden's worth of canopy behind the outermost houses; what is bought is
 * a ring deep enough that the fog, not the edge of the map, is what ends the
 * view — which is the entire point of having one.
 */
export function worthPlanting(patch: Rect, bounds: Rect): boolean {
  return rectsOverlap(patch, bounds);
}
